import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { axiosPrivate } from "../service";
import { useAuth } from "./useAuth";

export default function useAxios() {
    const { token, setToken } = useAuth();
    const navigate = useNavigate();
    const isRefreshing = useRef(false);
    const failedQueue = useRef<
        Array<{
            resolve: (value?: any) => void;
            reject: (reason?: any) => void;
        }>
    >([]);

    const processQueue = (error: any, token: string | null = null) => {
        failedQueue.current.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error);
            } else {
                resolve(token);
            }
        });

        failedQueue.current = [];
    };

    useEffect(() => {
        // Request interceptor to add token
        const requestIntercept = axiosPrivate.interceptors.request.use(
            (config) => {
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor for token refresh
        const responseIntercept = axiosPrivate.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    if (error.response.data.Error.includes("Refresh token not found.")) {
                        navigate("/auth/landing");
                        return Promise.reject(error);
                    }

                    if (isRefreshing.current) {
                        // If already refreshing, queue the request
                        return new Promise((resolve, reject) => {
                            failedQueue.current.push({ resolve, reject });
                        })
                            .then((token) => {
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                                return axiosPrivate(originalRequest);
                            })
                            .catch((err) => {
                                return Promise.reject(err);
                            });
                    }

                    originalRequest._retry = true;
                    isRefreshing.current = true;

                    try {
                        // Call token exchange endpoint
                        const response = await axiosPrivate.post("/v1/auth/token");
                        const newToken = response.data.Data;

                        setToken(newToken);
                        processQueue(null, newToken);

                        // Retry original request with new token
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return axiosPrivate(originalRequest);
                    } catch (refreshError) {
                        processQueue(refreshError, null);
                        setToken("");
                        // Redirect to login or handle auth failure
                        navigate("/auth/landing");
                        return Promise.reject(refreshError);
                    } finally {
                        isRefreshing.current = false;
                    }
                }

                return Promise.reject(error);
            }
        );

        // Cleanup interceptors on unmount
        return () => {
            axiosPrivate.interceptors.request.eject(requestIntercept);
            axiosPrivate.interceptors.response.eject(responseIntercept);
        };
    }, [token, setToken]);

    return { axiosPrivate };
}
